// Reusable input sanitizers for form fields
// eslint-disable-next-line no-control-regex -- intentional: strips ASCII control chars from input
const CTRL_REGEX = /[\u0000-\u001F\u007F]/g; // ASCII control
const ZERO_WIDTH_REGEX = /[\u200B-\u200F\u202A-\u202E\u2066-\u2069]/g; // zero-width & bidi
const ANGLE_BRACKETS_REGEX = /[<>]/g; // avoid HTML injection

export const stripUnsafe = (s = '') =>
  String(s)
    .replace(CTRL_REGEX, '')
    .replace(ZERO_WIDTH_REGEX, '')
    .replace(ANGLE_BRACKETS_REGEX, '');

export const collapseWhitespace = (s = '') => String(s).replace(/\s+/g, ' ').trim();

const clamp = (s, max) => (s.length > max ? s.slice(0, max) : s);

export const sanitizeEmail = (s = '') => {
  const v = collapseWhitespace(stripUnsafe(s)).toLowerCase().replace(/\s/g, '');
  return clamp(v, 254);
};

export const sanitizeName = (s = '') => {
  const v = collapseWhitespace(stripUnsafe(s)).replace(/[^\p{L}\p{M}\s.'-]/gu, '');
  return clamp(v, 64);
};

export const sanitizeAddressLine = (s = '') => {
  const v = collapseWhitespace(stripUnsafe(s)).replace(/[^0-9\p{L}\p{M}\s.,#\-/'&]/gu, '');
  return clamp(v, 100);
};

export const sanitizeCity = (s = '') => {
  const v = collapseWhitespace(stripUnsafe(s)).replace(/[^\p{L}\p{M}\s.'-]/gu, '');
  return clamp(v, 64);
};

export const sanitizeState = (s = '') => {
  const v = collapseWhitespace(stripUnsafe(s)).toUpperCase().replace(/[^A-Z\s-]/g, '');
  return clamp(v, 32);
};

export const sanitizePostalCode = (s = '') => {
  const v = stripUnsafe(s).replace(/\s+/g, '').replace(/[^0-9-]/g, '');
  const norm = v.replace(/-+/g, '-');
  return clamp(norm, 10); // 12345 or 12345-6789
};

export const sanitizeCountry = (s = '') => {
  const v = collapseWhitespace(stripUnsafe(s)).replace(/[^\p{L}\p{M}\s.'-]/gu, '');
  return clamp(v, 56);
};

// Country-aware helpers
export const normalizeCountryToISO = (s = '') => {
  const v = collapseWhitespace(stripUnsafe(s)).toLowerCase();
  if ((/^us$|united\s*states|u\.?s\.?a?\.?$/i).test(v)) return 'US';
  if ((/^ca$|canada$/i).test(v)) return 'CA';
  if ((/^gb$|^uk$|united\s*kingdom|great\s*britain$/i).test(v)) return 'GB';
  if ((/^au$|australia$/i).test(v)) return 'AU';
  return 'OTHER';
};

// Strict state when requested (e.g., US -> 2 letters). Otherwise fallback to generic sanitizeState.
export const sanitizeStateStrict = (s = '', { country, strict } = {}) => {
  const upper = collapseWhitespace(stripUnsafe(s)).toUpperCase();
  if (strict && country === 'US') {
    const code = upper.replace(/[^A-Z]/g, '').slice(0, 2);
    return code;
  }
  return sanitizeState(upper);
};

// Country postal formatting and patterns
export const formatPostalCode = (s = '', country = 'OTHER') => {
  const iso = normalizeCountryToISO(country);
  const raw = stripUnsafe(s);
  if (iso === 'US') {
    const t = raw.replace(/[^\d-]/g, '').replace(/\s+/g, '').replace(/-+/g, '');
    return t.length > 5 ? `${t.slice(0, 5)}-${t.slice(5, 9)}` : t.slice(0, 5);
  }
  if (iso === 'CA') {
    const t = raw.toUpperCase().replace(/[^A-Z0-9]/g, '');
    return t.replace(/^(\w\d\w)(\d\w\d).*/, '$1 $2').trim();
  }
  if (iso === 'GB') {
    return raw.toUpperCase().replace(/\s+/g, ' ').trim();
  }
  if (iso === 'AU') {
    return raw.replace(/[^\d]/g, '').slice(0, 4);
  }
  return sanitizePostalCode(raw);
};

export const getPostalPatternAndTitle = (country = 'OTHER') => {
  const iso = normalizeCountryToISO(country);
  if (iso === 'US') return { pattern: '^\\d{5}(-\\d{4})?$', title: 'US ZIP: 12345 or 12345-6789' };
  if (iso === 'CA') return { pattern: '^[A-Za-z]\\d[A-Za-z]\\s?\\d[A-Za-z]\\d$', title: 'Canada: A1A 1A1' };
  if (iso === 'GB') return { pattern: '^[A-Z]{1,2}\\d[A-Z\\d]?\\s?\\d[A-Z]{2}$', title: 'UK postcode (e.g., SW1A 1AA)' };
  if (iso === 'AU') return { pattern: '^\\d{4}$', title: 'Australia: 4 digits' };
  return {};
};

// Search query sanitizer: preserves diacritics and common search punctuation
export const sanitizeSearchQuery = (s = '', maxLen = 128) => {
  const base = collapseWhitespace(stripUnsafe(s));
  const safe = base.replace(/[^\p{L}\p{M}\p{N}\s.,'"@#&()+\-/_:]/gu, '');
  const normalized = safe.replace(/\s{2,}/g, ' ').trim();
  return clamp(normalized, maxLen);
};
